import React, { memo, useMemo, useRef } from 'react';
import { Text } from '@react-three/drei';
import { useFrame } from '../../utils/useFrameP1';

// ---------------------------------------------------------------------------
// UI-A T2: nomes do cubo — "COMPRADORES / VENDEDORES" nas zonas do chão e da lateral e
// títulos de eixo (TEMPO, PREÇO, FORÇA) junto às arestas.
//
// Variante por URL: ?nomes=a (padrão, discreta) ou ?nomes=b (com ▲▼ e pílula translúcida).
// Regras de desempenho: as strings são constantes (nunca reatribuídas); por quadro só se lê a
// distância da câmera (sem setState, sem alocação) e se escreve escala/visibilidade com dirty-check.
// Setas e triângulos são malhas (não glifos), então não dependem de fonte.
// ---------------------------------------------------------------------------

// ===== PARÂMETROS (ajuste aqui) ============================================
// Tamanhos em fração da LARGURA do cubo (width3D = 2200 por padrão).
export const CUBE_NAMES = {
  NAME_SIZE: 0.022,        // tamanho de COMPRADORES / VENDEDORES
  AXIS_SIZE: 0.016,        // tamanho dos títulos de eixo
  LETTER_SPACING: 0.18,    // espaçamento entre letras (caixa-alta)
  NAME_OPACITY: 0.65,      // 60–70%: cor da lateral (verde/vermelho)
  AXIS_OPACITY: 0.6,
  AXIS_COLOR: '#cfe9ff',
  // Posições (frações). Chão: nomes DENTRO do plano, encostados no lado esquerdo (longe dos números de força, na direita).
  FLOOR_NAME_X: 0.14,      // fração da largura a partir da aresta esquerda do chão
  FLOOR_NAME_Z: 0.5,       // fração de zLimit (frente = compradores, fundo = vendedores)
  // Lateral: nomes ACIMA da aresta de cima (os números de preço/força ficam dentro/embaixo).
  SIDE_NAME_Z: 0.5,        // fração de zLimit
  SIDE_NAME_GAP_Y: 0.07,   // fração da altura acima do topo da grade
  // Títulos de eixo (fora das arestas, além dos números)
  TIME_GAP_Z: 0.07,        // fração da largura além da borda da frente do chão
  PRICE_GAP_X: 0.05,       // fração da largura além da aresta direita da parede de fundo
  FORCE_FLOOR_GAP_X: 0.05, // fração da largura além da aresta direita do chão
  FORCE_SIDE_GAP_Y: 0.08,  // fração da altura abaixo da base da lateral
  // Distância da câmera (múltiplos da largura do cubo)
  SCALE_FROM: 1.2,         // a partir daqui os nomes crescem para continuar legíveis
  SCALE_MAX: 2.4,          // crescimento máximo
  HIDE_FROM: 8,            // acima disto (zoom out extremo) os nomes somem; maxDistance = 25000 ≈ 11,4×
  // Variante B
  PILL_COLOR: '#0a0e27',
  PILL_OPACITY: 0.5,
  TRI: 0.55,               // raio do triângulo (× tamanho do texto)
} as const;
// ============================================================================

export const readNamesVariant = (search: string): 'a' | 'b' => {
  try { return new URLSearchParams(search).get('nomes') === 'b' ? 'b' : 'a'; } catch { return 'a'; }
};

interface Props {
  startX: number; width: number; floorY: number; zLimit: number; sideX: number;
  topOfGrid: number; bottomOfGrid: number; axisX: number; wallZ: number; heightScale: number;
  buyersColor: string; sellersColor: string;
  /** Regra antiga: nomes do chão só com o chão ligado; da lateral só com a lateral ligada. */
  showFloor: boolean; showSide: boolean;
}

const HALF_PI = Math.PI / 2;
const estW = (n: number, size: number) => n * size * (0.62 + CUBE_NAMES.LETTER_SPACING);

const Tri = ({ r, rotZ, color, opacity }: { r: number; rotZ: number; color: string; opacity: number }) => (
  <mesh rotation={[0, 0, rotZ]} renderOrder={6}>
    <circleGeometry args={[r, 3, HALF_PI]} />
    <meshBasicMaterial color={color} transparent opacity={opacity} depthWrite={false} toneMapped={false} side={2} />
  </mesh>
);

/** Rótulo centrado (referência local: lê-se para +x). `arrows`: -1 só ←, 1 só →, 2 ambas, 0 nenhuma; `marker`: ▲/▼ à esquerda (B). */
const Label = ({ text, size, color, opacity, variant, arrows = 0, marker = 0 }: {
  text: string; size: number; color: string; opacity: number; variant: 'a' | 'b'; arrows?: -1 | 0 | 1 | 2; marker?: -1 | 0 | 1;
}) => {
  const w = estW(text.length, size);
  const g = size * 0.9;
  const r = size * CUBE_NAMES.TRI;
  return (
    <>
      {variant === 'b' && (
        <mesh position={[0, 0, -0.2]} renderOrder={4}>
          <planeGeometry args={[w + size * (marker ? 2.6 : 1.2), size * 1.7]} />
          <meshBasicMaterial color={CUBE_NAMES.PILL_COLOR} transparent opacity={CUBE_NAMES.PILL_OPACITY} depthWrite={false} toneMapped={false} />
        </mesh>
      )}
      <Text fontSize={size} color={color} fillOpacity={opacity} letterSpacing={CUBE_NAMES.LETTER_SPACING} anchorX="center" anchorY="middle" renderOrder={7}>
        {text}
      </Text>
      {(arrows === 1 || arrows === 2) && (
        <group position={[w / 2 + g, 0, 0]}><Tri r={r} rotZ={-HALF_PI} color={color} opacity={opacity} /></group>
      )}
      {(arrows === -1 || arrows === 2) && (
        <group position={[-(w / 2 + g), 0, 0]}><Tri r={r} rotZ={HALF_PI} color={color} opacity={opacity} /></group>
      )}
      {variant === 'b' && marker !== 0 && (
        <group position={[-(w / 2 + g), 0, 0]}><Tri r={r} rotZ={marker === 1 ? 0 : Math.PI} color={color} opacity={opacity} /></group>
      )}
    </>
  );
};

export const CubeNames = memo(({
  startX, width, floorY, zLimit, sideX, topOfGrid, bottomOfGrid, axisX, wallZ, heightScale, buyersColor, sellersColor, showFloor, showSide,
}: Props) => {
  const variant = useMemo(() => readNamesVariant(typeof window !== 'undefined' ? window.location.search : ''), []);
  const C = CUBE_NAMES;
  const nameSize = width * C.NAME_SIZE;
  const axisSize = width * C.AXIS_SIZE;

  // Grupos de escala (um por rótulo): registrados uma vez; por quadro só se escreve com dirty-check.
  const groups = useRef<(any | null)[]>([]);
  const lastScale = useRef(-1);
  const lastVisible = useRef<boolean | null>(null);
  const reg = (i: number) => (el: any) => { groups.current[i] = el; };

  useFrame((state) => {
    const p = state.camera.position;
    const dist = Math.sqrt(p.x * p.x + p.y * p.y + p.z * p.z);
    const rel = dist / width;
    const visible = rel <= C.HIDE_FROM;
    const s = Math.min(C.SCALE_MAX, Math.max(1, rel / C.SCALE_FROM));
    const arr = groups.current;
    if (visible !== lastVisible.current) {
      lastVisible.current = visible;
      for (let i = 0; i < arr.length; i++) if (arr[i]) arr[i].visible = visible;
    }
    if (visible && Math.abs(s - lastScale.current) > 0.005) {
      lastScale.current = s;
      for (let i = 0; i < arr.length; i++) if (arr[i]) arr[i].scale.setScalar(s);
    }
  });

  const fy = floorY + 2;
  const flat: [number, number, number] = [-HALF_PI, 0, 0];
  const side: [number, number, number] = [0, HALF_PI, 0];
  const nameOp = C.NAME_OPACITY;
  const axOp = C.AXIS_OPACITY;
  const floorX = startX + width * C.FLOOR_NAME_X;
  const sideY = topOfGrid + heightScale * C.SIDE_NAME_GAP_Y;

  return (
    <>
      {/* CHÃO — zonas (compradores na frente, vendedores no fundo) */}
      {showFloor && (
        <>
      <group ref={reg(0)} position={[floorX, fy, zLimit * C.FLOOR_NAME_Z]} rotation={flat}>
        <Label text="COMPRADORES" size={nameSize} color={buyersColor} opacity={nameOp} variant={variant} marker={1} />
      </group>
      <group ref={reg(1)} position={[floorX, fy, -zLimit * C.FLOOR_NAME_Z]} rotation={flat}>
        <Label text="VENDEDORES" size={nameSize} color={sellersColor} opacity={nameOp} variant={variant} marker={-1} />
      </group>

        </>
      )}

      {/* LATERAL — zonas acima da aresta de cima (compradores em +Z, vendedores em −Z) */}
      {showSide && (
        <>
      <group ref={reg(2)} position={[sideX, sideY, zLimit * C.SIDE_NAME_Z]} rotation={side}>
        <Label text="COMPRADORES" size={nameSize} color={buyersColor} opacity={nameOp} variant={variant} marker={1} />
      </group>
      <group ref={reg(3)} position={[sideX, sideY, -zLimit * C.SIDE_NAME_Z]} rotation={side}>
        <Label text="VENDEDORES" size={nameSize} color={sellersColor} opacity={nameOp} variant={variant} marker={-1} />
      </group>

        </>
      )}

      {/* TÍTULOS DE EIXO */}
      {/* TEMPO: além da borda da frente do chão, seta para o presente (+X) */}
      {showFloor && (
      <group ref={reg(4)} position={[startX + width / 2, fy, zLimit + width * C.TIME_GAP_Z]} rotation={flat}>
        <Label text="TEMPO" size={axisSize} color={C.AXIS_COLOR} opacity={axOp} variant={variant} arrows={1} />
      </group>
      )}
      {/* PREÇO: fora da aresta direita da parede de fundo, lê-se para cima, seta para cima */}
      <group ref={reg(5)} position={[axisX + width * C.PRICE_GAP_X, 0, wallZ + 1]} rotation={[0, 0, HALF_PI]}>
        <Label text="PREÇO" size={axisSize} color={C.AXIS_COLOR} opacity={axOp} variant={variant} arrows={1} />
      </group>
      {/* FORÇA (chão): fora da aresta direita; setas dos dois lados (compradores à frente, +Z) */}
      {showFloor && (
      <group ref={reg(6)} position={[axisX + width * C.FORCE_FLOOR_GAP_X, fy, 0]} rotation={[0, -HALF_PI, 0]}>
        <group rotation={flat}>
          <Label text="FORÇA" size={axisSize} color={C.AXIS_COLOR} opacity={axOp} variant={variant} arrows={2} />
        </group>
      </group>
      )}
      {/* FORÇA (lateral): abaixo da base; ← aponta para compradores (+Z) */}
      {showSide && (
      <group ref={reg(7)} position={[sideX, bottomOfGrid - heightScale * C.FORCE_SIDE_GAP_Y, 0]} rotation={side}>
        <Label text="FORÇA" size={axisSize} color={C.AXIS_COLOR} opacity={axOp} variant={variant} arrows={2} />
      </group>
      )}
    </>
  );
});
